import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Shield, Loader2, ArrowLeft, Ban, Plus, ShieldOff, MapPin } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlocklistPage = () => {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState('ip');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [adding, setAdding] = useState(false);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/blocklist`);
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      toast.error('Failed to load blocklist.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchList(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!value.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/admin/blocklist`, {
        type,
        value: value.trim(),
        reason: reason.trim() || null,
      });
      setValue('');
      setReason('');
      toast.success(`${type.toUpperCase()} blocked`);
      fetchList();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Block failed');
    } finally {
      setAdding(false);
    }
  };

  const remove = async (id, value) => {
    if (!window.confirm(`Unblock ${value}?`)) return;
    try {
      await axios.delete(`${API}/admin/blocklist/${id}`);
      toast.success('Removed from blocklist');
      fetchList();
    } catch {
      toast.error('Remove failed');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <div className="max-w-4xl mx-auto p-6 sm:p-10">
        <button onClick={() => navigate('/admin/home')} className="text-xs text-[#71717A] hover:text-[#00F0FF] inline-flex items-center gap-1 mb-2">
          <ArrowLeft size={12} /> Admin home
        </button>
        <h1 className="text-3xl font-heading font-bold flex items-center gap-3 mb-2">
          <Shield className="text-red-400" size={28} /> Blocklist
        </h1>
        <p className="text-sm text-[#A1A1AA] mb-6">
          Blocked IPs and emails are refused at checkout with a 403. This is your first line of defense against piracy buyers who create new names and emails to bypass a block.
        </p>

        <form onSubmit={add} className="bg-[#121214] border border-[#27272A] rounded-lg p-5 mb-6" data-testid="block-add-form">
          <div className="grid grid-cols-1 sm:grid-cols-[110px_1fr_1fr_auto] gap-3">
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="bg-[#0F0F11] border border-[#27272A] rounded-md px-3 py-2 text-sm"
              data-testid="block-type-select"
            >
              <option value="ip">IP</option>
              <option value="email">Email</option>
            </select>
            <Input
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={type === 'ip' ? '203.0.113.10' : 'bad-actor@example.com'}
              required
              className="bg-[#0F0F11] border-[#27272A]"
              data-testid="block-value-input"
            />
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional)"
              className="bg-[#0F0F11] border-[#27272A]"
              data-testid="block-reason-input"
            />
            <Button
              type="submit"
              disabled={adding}
              className="bg-red-500 hover:bg-red-600 text-white rounded-full"
              data-testid="block-add-btn"
            >
              {adding ? <Loader2 className="animate-spin" size={16} /> : (<><Plus size={14} className="mr-1" /> Block</>)}
            </Button>
          </div>
        </form>

        {loading ? (
          <div className="text-center py-16 text-[#71717A]"><Loader2 className="mx-auto animate-spin mb-3" /> Loading…</div>
        ) : entries.length === 0 ? (
          <div className="text-center py-16 bg-[#121214] border border-[#27272A] rounded-lg text-sm text-[#71717A]" data-testid="blocklist-empty">
            No blocked identities yet.
          </div>
        ) : (
          <div className="bg-[#121214] border border-[#27272A] rounded-lg overflow-x-auto">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-[#0F0F11] text-[10px] uppercase tracking-wider text-[#71717A]">
                <tr>
                  <th className="text-left px-4 py-2">Type</th>
                  <th className="text-left px-4 py-2">IP / Email</th>
                  <th className="text-left px-4 py-2">Location</th>
                  <th className="text-left px-4 py-2">Reason</th>
                  <th className="text-left px-4 py-2">Blocked on</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id} className="border-t border-[#27272A]" data-testid={`block-row-${e.id}`}>
                    <td className="px-4 py-3">
                      <span className="text-[10px] uppercase px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/30">{e.type}</span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs break-all" data-testid={`block-value-${e.id}`}>{e.value}</td>
                    <td className="px-4 py-3 text-xs text-[#A1A1AA]" data-testid={`block-location-${e.id}`}>
                      {e.type === 'ip' && (e.country || e.city) ? (
                        <span className="inline-flex items-center gap-1">
                          <MapPin size={12} className="text-[#71717A]" />
                          {[e.city, e.region, e.country].filter(Boolean).join(', ') || '—'}
                        </span>
                      ) : (
                        <span className="text-[#52525B]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-[#A1A1AA]">{e.reason || '—'}</td>
                    <td className="px-4 py-3 text-xs text-[#71717A] whitespace-nowrap">{new Date(e.blocked_at).toLocaleString()}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => remove(e.id, e.value)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 text-xs whitespace-nowrap"
                        title="Restore website access"
                        data-testid={`unblock-btn-${e.id}`}
                      >
                        <ShieldOff size={12} /> Unblock
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlocklistPage;
